// lib/studio/runStudioGeneration.ts
//
// The Studio generation recipe, in ONE place.
//
// Running an image generation on this app's own rails is not a call, it is a
// sequence: resolve or create a project, make sure it has a generation
// workspace, load the source as a visible layer, select the model and set its
// prompt, frame the workspace, run the model, wait for the task, then work out
// which layer is the output. Master's RUN_STUDIO_STEP worked that sequence out
// against the code (docs/specs/master-agent-orchestrator.md records the
// derivation and the traps: the server already provisions a workspace on
// project create, so adding one blindly gives every project two overlapping
// workspaces; and an in-place Power modifies a layer instead of adding one, so
// "newest non-source layer" misses its output).
//
// That knowledge is worth exactly one implementation. Slides needs the same
// sequence to build its counter-proposal, and a second copy would drift from
// this one the first time a trap is fixed on one side only - which is how the
// counter-proposal came to hold its own duplicate image call in the first place.
//
// What stays OUT of here is everything Master-specific: tagging the run to a
// master session, the loading skeleton, mirroring prompts into the project
// conversation, delivering a card into a timeline. Those are how MASTER
// presents a generation, not how the app performs one.
import { FLAGSHIP_STUDIO_MODEL_ID, resolveRegistryModelIdFromRuntimeName } from '#lib/model-registry';
import { isNewMaiImageModel } from '#lib/maiImage';
import { getDefaultParamsForModel } from '#lib/models';
import type { KernelHandle, RuntimeState, LockableFile } from '#types/types';

export interface StudioGenerationRequest {
  /** Reuse an existing project across the steps of one run. Omit to create one. */
  projectId?: string | null;
  /** Name for a project this call creates. Ignored when projectId resolves. */
  projectName?: string | null;
  /**
   * The image to edit, as an INTERNAL asset url (/api/assets/...). Omit for a
   * text-to-image generation with no base layer.
   */
  sourceImageUrl?: string | null;
  /** Distinct vetted inspiration; never replaces the source workspace image. */
  referenceImages?: LockableFile[];
  referenceImageUrls?: string[];
  /** The instruction, in English. Required: no model here is run blind. */
  prompt: string;
  /** A registry model id. Falls back to the flagship editor when unknown. */
  model?: string | null;
  /**
   * Model parameters to set for this generation, beyond the prompt.
   *
   * The Studio pipeline runs a model with the parameters currently held for it,
   * which start from that model's control-panel defaults. Those defaults are
   * chosen for a designer working a canvas, and they are not always right for a
   * caller running the model headlessly - so a caller that needs a specific
   * setting states it here rather than inheriting silently.
   */
  modelParams?: Record<string, unknown> | null;
  /** "16:9", "4:3", a bare number, or "auto". Sets the workspace FRAME. */
  aspectRatio?: string | null;
  /**
   * Generate into a NEW workspace instead of the project's current one.
   *
   * The default reuses the existing workspace, which is right for a run whose
   * steps build on each other. It is wrong for repeated attempts at the SAME
   * image: the model edits the visible content of the generation area, so a
   * second attempt in the same workspace edits the first attempt's output with
   * a second copy of the source stacked on it, not the source.
   *
   * Measured on the first live run of the Slides proposal loop: three attempts
   * left six layers in one workspace (Source, output, Source, output, Source,
   * output), and drafts 2 and 3 were edits of a composite nobody asked for.
   */
  freshWorkspace?: boolean;
  abortSignal?: AbortSignal;
}

export interface StudioGenerationResult {
  projectId: string;
  workspaceId: string;
  /** The produced image, or null when no output layer could be resolved. */
  outputImageUrl: string | null;
  /** The model actually used, which is not always the one requested. */
  model: string;
}

/** How long a finished task's saved layer may take to reach the canvas before the run reports no image. */
const OUTPUT_ARRIVAL_WAIT_MS = 90_000;

/** "16:9" | "1.78" | "auto" -> a ratio, or null when there is nothing to set. */
export function parseAspectRatio(raw: string | null | undefined): number | null {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value || value.toLowerCase() === 'auto') return null;
  if (value.includes(':')) {
    // Both sides positive: "-16:9" used to yield -1.78, which one caller filtered
    // with two extra variables and the other passed straight to
    // setWorkspaceAspectRatio. A ratio is positive or it is not a ratio.
    const [w, h] = value.split(':').map(Number);
    return (w ?? 0) > 0 && (h ?? 0) > 0 ? (w as number) / (h as number) : null;
  }
  const n = parseFloat(value);
  return !Number.isNaN(n) && n > 0 ? n : null;
}

/**
 * Run one generation on the Studio pipeline and return its output.
 *
 * The caller owns presentation. This owns the sequence, and every step of it is
 * the real thing: a real project, a real workspace, a real version, a real
 * GenerationLog and GenerationTaskSnapshot through runModel.
 */
export async function runStudioGeneration(
  kernel: KernelHandle,
  request: StudioGenerationRequest
): Promise<StudioGenerationResult> {
  const prompt = request.prompt?.trim() ?? '';
  if (!prompt) {
    throw new Error('runStudioGeneration: "prompt" is required and must be a non-empty string.');
  }

  // 1) Resolve or create the project. A provided id is only reused when it is
  //    really in the inventory, so a stale id from an earlier run creates a new
  //    project instead of throwing halfway through.
  const provided = request.projectId?.trim() ?? '';
  let projectId = provided && kernel.getInventory().Project.registry[provided] ? provided : '';
  if (!projectId && provided) {
    // The registry is not the whole truth. Boot hydration loads the 16 most
    // recent projects, so a caller holding a DURABLE id - Slides persists its
    // companion project on the deck doc - finds nothing there on a fresh tab, or
    // for anyone with more than sixteen projects. Falling straight through to
    // createEntity would then make a new "<Deck>, proposals" project on every
    // visit, which is the scattering the persisted id exists to prevent. Ask the
    // server before concluding the project is gone.
    await kernel.syncEntity?.('Project', provided).catch(() => undefined);
    if (kernel.getInventory().Project.registry[provided]) projectId = provided;
  }
  if (!projectId) {
    if (!kernel.createEntity) {
      throw new Error('runStudioGeneration: project creation is not available on the kernel handle.');
    }
    const name =
      request.projectName?.trim() ||
      prompt.split(/\s+/).slice(0, 6).join(' ').slice(0, 60) ||
      'Studio run';
    const created = await kernel.createEntity('Project', { name });
    projectId = typeof created?.id === 'string' ? created.id.trim() : '';
    if (!projectId) throw new Error('runStudioGeneration: project creation failed (missing id).');
    await kernel.syncEntity?.('Project', projectId).catch(() => undefined);
  }

  // 2) Ensure a generation workspace. Project.CREATE provisions one SERVER-side,
  //    so look for it before adding: adding blindly gave master-built projects
  //    two overlapping workspaces, both named "1", ~300ms apart.
  let workspaceId = request.freshWorkspace ? '' : kernel.getProjectUIWorkspaceId(projectId);
  if (request.freshWorkspace) {
    workspaceId = kernel.handleAddWorkspace(projectId, { logSource: 'runStudioGeneration:fresh' }) ?? '';
  } else if (!workspaceId) {
    await kernel.syncEntity?.('Project', projectId).catch(() => undefined);
    const row = kernel.getInventory().Project.registry[projectId] as { workspaceIds?: unknown } | undefined;
    const existing = Array.isArray(row?.workspaceIds)
      ? (row.workspaceIds as unknown[]).find((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : undefined;
    if (existing) {
      workspaceId = existing;
      await kernel.syncEntity?.('StudioWorkspace', existing).catch(() => undefined);
      kernel.setProjectUIWorkspaceId(projectId, existing);
    } else {
      workspaceId = kernel.handleAddWorkspace(projectId, { logSource: 'runStudioGeneration' }) ?? '';
    }
  }
  if (!workspaceId) {
    throw new Error('runStudioGeneration: could not resolve or create a workspace for the generation.');
  }

  // 3) The source layer, when this is an edit rather than a text-to-image.
  const sourceImageUrl = request.sourceImageUrl?.trim() ?? '';
  if (sourceImageUrl) {
    await kernel.addLayerFromUrl(workspaceId, sourceImageUrl, { name: 'Source', source: 'quickstart' });
  }

  // 4) Model + params, as an OVERRIDE rather than by mutating global state.
  //
  //    runModel takes a RuntimeState (types.ts, `runtimeStateOverride`) and
  //    resolves both the model (useModels.ts: `runtimeStateOverride?.diffusionModel
  //    || getDiffusionModel()`) and the params from it. This used to call
  //    setDiffusionModel + setModelParameter instead, then snapshot and restore
  //    the params in a finally - working around a signature that never needed
  //    working around, and admitting in its own comment that the SELECTED model
  //    could not be restored at all, so a headless proposal moved the operator's
  //    Studio model selector for the rest of their session.
  //
  //    The override also isolates the run: with no `referenceImages` the pipeline
  //    falls back to whatever the operator has attached in the UI, and it skips
  //    clearing their unlocked reference files only when an override is present.
  //    `buildQuickPowerRuntimeState` passes `referenceImages: []` for exactly this
  //    reason, and this is the same kind of caller.
  //
  //    The override REPLACES the params map, so the merge has to be complete:
  //    handleQueueEditDiffusion throws "Missing model params in array" on a model
  //    key it cannot find.
  const modelParams = kernel.getModelParams?.() || {};
  const rawRequested = request.model?.trim() ?? '';
  const requested = resolveRegistryModelIdFromRuntimeName(rawRequested) ?? rawRequested;
  const model = requested && (modelParams[requested] || isNewMaiImageModel(requested)) ? requested : FLAGSHIP_STUDIO_MODEL_ID;
  const baseParams = modelParams[model] ?? getDefaultParamsForModel(model)?.();
  const referenceImages = [...(request.referenceImages ?? [])];
  const downloadedReferences = await Promise.all((request.referenceImageUrls ?? []).slice(0, 3).map(async (url, index) => {
    if (!url.startsWith('/api/assets/')) throw new Error('Studio references must be internal assets.');
    const response = await fetch(url, { signal: request.abortSignal });
    if (!response.ok) throw new Error('A Studio reference image could not be loaded.');
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) throw new Error('A Studio reference is not an image.');
    return { id: url, locked: true, file: new File([blob], `brand-reference-${index + 1}.png`, { type: blob.type }) };
  }));
  referenceImages.push(...downloadedReferences);
  request.abortSignal?.throwIfAborted();
  const runtimeState = {
    diffusionModel: model,
    parameters: {
      [model]: { ...(baseParams ?? {}), prompt, ...(request.modelParams ?? {}) },
    },
    referenceImages,
  } as RuntimeState;

  // Edit models honour the workspace FRAME, not a model-level aspect parameter,
  // so this is what actually reshapes the output - and what decides what the
  // model SEES. "keep this unit, change these things" only means anything if the
  // unit is what the model was shown, so a caller that knows its source's shape
  // should pass it.
  const ratio = parseAspectRatio(request.aspectRatio);
  if (ratio && typeof kernel.setWorkspaceAspectRatio === 'function') {
    try {
      kernel.setWorkspaceAspectRatio(projectId, workspaceId, ratio);
    } catch {
      /* best-effort framing */
    }
  }

  // 5) Run it, and fail loudly on a failed task rather than returning a null
  //    output that the caller reads as "no image this time".
  const { tasks } = await kernel.runModel(workspaceId, runtimeState, undefined, request.abortSignal);
  const results = await Promise.all(tasks.map((task) => kernel.waitForTask(task.id)));
  const failed = results.find((r) => r?.status === 'FAILURE');
  if (failed) throw failed.error || new Error('runStudioGeneration: generation task failed.');

  // 6) Resolve the output: the most recently updated layer. Newest-UPDATED
  //    rather than newest-ADDED, because the in-place Powers (upscale, enhance,
  //    vectorize) modify a layer instead of adding one, and a "newest non-source
  //    layer" rule delivers nothing for them.
  const newestLayerUrl = (): string | null => {
    try {
      const layers = kernel.getLayersInWorkspaceContext?.(workspaceId) || [];
      const candidates = layers
        .map((layer) => ({
          url:
            (layer as { imageSrc?: string }).imageSrc ||
            (layer as { url?: string }).url ||
            (layer as { imageUrl?: string }).imageUrl ||
            '',
          stamp:
            (layer as { updatedAt?: string }).updatedAt || (layer as { createdAt?: string }).createdAt || '',
        }))
        .filter((c) => c.url)
        .sort((a, b) => (a.stamp < b.stamp ? -1 : a.stamp > b.stamp ? 1 : 0));
      const newest = candidates[candidates.length - 1];
      return newest && newest.url && newest.url !== sourceImageUrl ? newest.url : null;
    } catch {
      return null;
    }
  };
  // A server-saved output reaches the canvas over the socket, seconds after the task reports success,
  // so the canvas can still hold only the source. The task's own completion names what it produced.
  const completed = results
    .map((result) => (result?.status === 'SUCCESS' ? (result.data as { layer?: { imageSrc?: string }; urls?: string[] } | undefined) : undefined))
    .map((data) => data?.layer?.imageSrc || data?.urls?.find(Boolean) || '')
    .find((url) => url && url !== sourceImageUrl) || null;
  let outputImageUrl = newestLayerUrl() ?? completed;
  for (const deadline = Date.now() + OUTPUT_ARRIVAL_WAIT_MS; !outputImageUrl && Date.now() < deadline;) {
    request.abortSignal?.throwIfAborted();
    await new Promise((resolve) => setTimeout(resolve, 500));
    outputImageUrl = newestLayerUrl();
  }

  return { projectId, workspaceId, outputImageUrl, model };
}
